import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Divider
} from '@mui/material';
import {
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';
import { useUserPermissions } from '../../../hooks/useUserPermissions';
import { API_URL } from '../../../utils/config';

interface Invoice {
  id: string;
  docNumber?: string;
  transactionDate?: string;
  dueDate?: string;
  totalAmount?: number;
  balance?: number;
  currencyCode?: string;
  currencyName?: string;
  depositToAccount?: string;
  privateNote?: string;
  customerMemo?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface CustomerDetails {
  id: string;
  syncToken?: string;
  displayName: string;
  givenName?: string;
  middleName?: string;
  familyName?: string;
  companyName?: string;
  printOnCheckName?: string;
  isActive?: boolean;
  isTaxable?: boolean;
  job?: boolean;
  billWithParent?: boolean;
  contact?: {
    primaryPhone?: string;
    alternatePhone?: string;
    mobilePhone?: string;
    fax?: string;
    primaryEmail?: string;
    website?: string;
  };
  billingAddress?: {
    id?: string;
    line1?: string;
    line2?: string;
    line3?: string;
    line4?: string;
    line5?: string;
    city?: string;
    country?: string;
    countrySubDivisionCode?: string;
    postalCode?: string;
    latitude?: number;
    longitude?: number;
  } | null;
  financial?: {
    balance?: number;
    balanceWithJobs?: number;
    currencyCode?: string;
    currencyName?: string;
    preferredDeliveryMethod?: string;
    resaleNum?: string;
  };
  tax?: {
    defaultTaxCodeValue?: string;
    taxExemptionReasonId?: number;
  };
  references?: {
    paymentMethod?: { value?: string; name?: string } | null;
    salesTerms?: { value?: string; name?: string } | null;
    parent?: { value?: string; name?: string } | null;
    level?: number;
  };
  metadata?: {
    quickBooksCreateTime?: string;
    quickBooksLastUpdatedTime?: string;
    platformCreatedAt?: string;
    platformUpdatedAt?: string;
    syncStatus?: string;
    lastSyncAt?: string;
    syncError?: string;
  };
  invoices?: Invoice[];
}

export const CustomerDetailsPage: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { hasPermission } = useUserPermissions();

  const [customer, setCustomer] = useState<CustomerDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCustomerDetails = async () => {
    if (!customerId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(`${API_URL}/api/accounting/admin/customers/${customerId}`);

      if (!response || !response.ok) {
        if (response?.status === 403) {
          throw new Error('Access denied. You do not have permission to view this customer.');
        }
        if (response?.status === 404) {
          throw new Error('Customer not found');
        }
        throw new Error('Failed to fetch customer details');
      }

      const result = await response.json();
      setCustomer(result.data);
    } catch (err: any) {
      console.error('Error fetching customer details:', err);
      setError(err.message || 'Failed to fetch customer details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerDetails();
  }, [customerId]);

  const formatCurrency = (amount?: number, currency: string = 'USD') => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };


  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!customer) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="info">
          No customer data available.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Link
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/admin/hub')}
        >
          Admin Hub
        </Link>
        <Typography color="text.primary">{customer.displayName}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <PersonIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1">
                {customer.displayName}
              </Typography>
              {customer.companyName && customer.companyName !== customer.displayName && (
                <Typography variant="body1" color="text.secondary">
                  {customer.companyName}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {customer.isActive === false && (
              <Chip label="Inactive" color="error" />
            )}
            <IconButton onClick={fetchCustomerDetails} title="Refresh">
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Contact Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {customer.contact?.primaryPhone && (
                <>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Primary Phone</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body2">{customer.contact.primaryPhone}</Typography>
                  </Grid>
                </>
              )}
              {customer.contact?.mobilePhone && (
                <>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Mobile Phone</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body2">{customer.contact.mobilePhone}</Typography>
                  </Grid>
                </>
              )}
              {customer.contact?.primaryEmail && (
                <>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Primary Email</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body2">{customer.contact.primaryEmail}</Typography>
                  </Grid>
                </>
              )}
              {customer.contact?.website && (
                <>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Website</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body2">{customer.contact.website}</Typography>
                  </Grid>
                </>
              )}

              {/* Billing Address */}
              {customer.billingAddress && (
                <>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Billing Address
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Box>
                      {customer.billingAddress.line1 && (
                        <Typography variant="body2">{customer.billingAddress.line1}</Typography>
                      )}
                      {customer.billingAddress.line2 && (
                        <Typography variant="body2">{customer.billingAddress.line2}</Typography>
                      )}
                      {customer.billingAddress.line3 && (
                        <Typography variant="body2">{customer.billingAddress.line3}</Typography>
                      )}
                      {(customer.billingAddress.city || customer.billingAddress.countrySubDivisionCode || customer.billingAddress.postalCode) && (
                        <Typography variant="body2">
                          {[
                            customer.billingAddress.city,
                            customer.billingAddress.countrySubDivisionCode,
                            customer.billingAddress.postalCode
                          ].filter(Boolean).join(', ')}
                        </Typography>
                      )}
                      {customer.billingAddress.country && (
                        <Typography variant="body2">{customer.billingAddress.country}</Typography>
                      )}
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Financial Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Financial Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {customer.financial?.currencyCode && (
                <>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Currency</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body2">
                      {customer.financial.currencyName || customer.financial.currencyCode} ({customer.financial.currencyCode})
                    </Typography>
                  </Grid>
                </>
              )}
              <Grid item xs={4}>
                <Typography variant="body2" color="text.secondary">Balance</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography
                  variant="body2"
                  sx={{
                    color: customer.financial?.balance === 0 ? 'green' :
                           customer.financial?.balance && customer.financial.balance > 0 ? 'error.main' :
                           'text.primary'
                  }}
                >
                  {formatCurrency(customer.financial?.balance, customer.financial?.currencyCode)}
                </Typography>
              </Grid>
              {customer.references?.salesTerms?.name && (
                <>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Sales Terms</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body2">{customer.references.salesTerms.name}</Typography>
                  </Grid>
                </>
              )}
              {customer.references?.paymentMethod?.name && (
                <>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Payment Method</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body2">{customer.references.paymentMethod.name}</Typography>
                  </Grid>
                </>
              )}
              {customer.financial?.preferredDeliveryMethod && (
                <>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Delivery Method</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body2">{customer.financial.preferredDeliveryMethod}</Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Invoices Table */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ReceiptIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Invoices</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                ({customer.invoices?.length || 0})
              </Typography>
            </Box>
            {customer.invoices && customer.invoices.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>HHTL Invoice #</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Balance</TableCell>
                      <TableCell>Currency</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customer.invoices.map((invoice) => (
                      <TableRow
                        key={invoice.id}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                        onClick={() => navigate(`/admin/hub/accounting/invoices/${invoice.id}`)}
                      >
                        <TableCell>
                          <Chip
                            label={invoice.docNumber || invoice.id}
                            size="small"
                            clickable
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/hub/accounting/invoices/${invoice.id}`);
                            }}
                            sx={{
                              fontFamily: 'monospace',
                              backgroundColor: 'rgba(0, 45, 96, 0.08)',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 45, 96, 0.15)',
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>{formatDate(invoice.transactionDate)}</TableCell>
                        <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {customer.displayName}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(invoice.totalAmount, invoice.currencyCode)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{
                              color: invoice.balance === 0 ? 'green' :
                                     invoice.balance && invoice.balance > 0 ? 'error.main' :
                                     'text.primary'
                            }}
                          >
                            {formatCurrency(invoice.balance, invoice.currencyCode)}
                          </Typography>
                        </TableCell>
                        <TableCell>{invoice.currencyCode || 'USD'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No invoices found for this customer
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};